import { FunctionComponent, useState, useRef, useEffect } from 'react';
import styles from "./ListIcons.module.css";
import cn from 'classnames';
import {Image} from 'react-bootstrap';
import SidebarLayout from '../../../component/common/Layout/SidebarLayout/SidebarLayout';
import TextHeader from '../../../component/common/TextHeader/TextHeader';
import ContentTabs from '../../../component/common/ContentTabs/ContentTabs';
import CustomButton, { ButtonFont, ButtonSize, ButtonType } from '../../../component/common/CustomButton/CustomButton';
import { ASSESTS } from '../../../constant/assest';
import ContentManagementCard from '../../../component/UI/ContentManagementCard/ContentManagementCard';
import ContentManagementCardSkeleton from '../../../component/UI/ContentManagementCard/ContentManagementCardSkeleton';
import UploadNewContent, { categoryOptions } from './component/UploadNewContent/UploadNewContent';
import DeleteModal from '../../../component/common/DeleteModal/DeleteModal';
import FilterSelect from '../../../component/common/FilterSelect/FilterSelect';
import SearchInput from '../../../component/common/SearchInput/SearchInput';
import ReviewPopup from '../../../component/UI/ReviewPopup/ReviewPopup';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchContentList, fetchContentById, updateContent, deleteContent } from '../../../redux/slices/listIcon/actions';
import { clearMessage, resetUploadStatus } from '../../../redux/slices/listIcon/slice';
import { IContentItem } from '../../../redux/slices/listIcon/types';
import { useDebounce } from '../../../hooks/useDebounce';
import { toast } from 'react-toastify';
import Pagination from "../../../component/UI/Pagination/Pagination";
import { usePagination } from '../../../hooks/usePagination';
import ConfirmationModal from '../../../component/common/ConfirmationModal/ConfirmationModal';

const ListIcons: FunctionComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { data, dataLoading, contentDetails, contentDetailsLoading, contentUploading, contentUpdateLoading, contentUpdateError, contentDeleteMessage, contentDeleteError, contentUpdateMessage, error } = useSelector((state: RootState) => state.listIcon);

    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [activeContentTab, setActiveContentTab] = useState('All Content');
    const [showReviewPopup, setShowReviewPopup] = useState<boolean>(false);
    const [showApprovePopup, setShowApprovePopup] = useState<boolean>(false);
    const [showRejectPopup, setShowRejectPopup] = useState<boolean>(false);
    const [contentToActOn, setContentToActOn] = useState<IContentItem | null>(null);
    const [actionData, setActionData] = useState<any>(null);
    const uploadRef = useRef<HTMLDivElement | null>(null);

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search.trim(), 500);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const prevDebouncedSearchRef = useRef<string | undefined>(undefined);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        const searchChanged = prevDebouncedSearchRef.current !== debouncedSearch;
        if (searchChanged && page !== 1) {
            setPage(1);
            return;
        }
        prevDebouncedSearchRef.current = debouncedSearch;
        
        const params: any = {
            page,
            limit,
            search: debouncedSearch,
        };

        if (categoryFilter !== 'all') {
            params.category = categoryFilter;
        }

        let tab: string | undefined;
        switch(activeContentTab) {
            case 'All Content':
                tab = 'all';
                break;
            case 'My Content':
                tab = 'my';
                break;
            case 'Sponsored Content':
                tab = 'sponsored';
                break;
            case 'Pending Approval':
                tab = 'pending';
                break;
        }

        params.tab = tab;
        params.status =  statusFilter !== 'all' ? statusFilter : undefined;

        dispatch(fetchContentList(params));
        window.scrollTo(0,0);
    }, [dispatch, page, limit, debouncedSearch, statusFilter, categoryFilter, activeContentTab, refreshKey]);

    useEffect(() => {
        // if (contentUploadMessage) {
        if (contentUploading === 'succeeded') {            
            if (page !== 1) {
                setPage(1);
            } else {
                setRefreshKey(k => k + 1);
            }
            dispatch(resetUploadStatus());
        }
        if (contentUpdateMessage) {
            toast.success(contentUpdateMessage);
            dispatch(clearMessage());
        }
        if (contentDeleteMessage) {
            toast.success(contentDeleteMessage);
            dispatch(clearMessage());
        }
        if (contentUpdateError) {
            toast.error(contentUpdateError);
            dispatch(clearMessage());
        }
        if (contentDeleteError) {
            toast.error(contentDeleteError);
            dispatch(clearMessage());
        }
    }, [contentUpdateMessage, contentDeleteMessage, contentUpdateError, contentDeleteError, dispatch, page, contentUploading]);

    const handleScrollToUpload = () => {
        if (uploadRef.current) {
            uploadRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const handleView = (item: IContentItem) => {
        dispatch(fetchContentById(item.id));
        setShowReviewPopup(true);
    };

    const handleDelete = (item: IContentItem) => {
        setContentToActOn(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (contentToActOn) {
            dispatch(deleteContent(contentToActOn.id)).unwrap().then(()=> {
                setShowReviewPopup(false);
                if (page !== 1) {
                    setPage(1);
                } else {
                    setRefreshKey(k => k + 1);
                }
                window.scrollTo(0,0);
            })
            .catch((err)=> console.error('delete failed:', err));
            setShowDeleteModal(false);
            setContentToActOn(null);          
        }
    };

    const handleApprove = (item: IContentItem) => {
        setContentToActOn(item);
        setShowApprovePopup(true);
    };

    const confirmApprove = () => {
        if (actionData) {
            dispatch(updateContent(actionData));
        } else if (contentToActOn) {
            dispatch(updateContent({ id: contentToActOn.id, status: 'published'}));
        }
        setShowApprovePopup(false);
        setContentToActOn(null);
        setActionData(null);
    };

    const handleReject = (item: IContentItem) => {
        setContentToActOn(item);
        setShowRejectPopup(true);
    };

    const confirmReject = () => {
        if (actionData) {
            dispatch(updateContent(actionData));
        } else if (contentToActOn) {
            dispatch(updateContent({ id: contentToActOn.id, status: 'rejected' }));
        }
        setShowRejectPopup(false);
        setContentToActOn(null);
        setActionData(null);
    };

    const handleUpdateFromPopup = (data: any) => {
        setActionData(data);
        // setShowReviewPopup(false);
        if (data.status === 'published') {
            setShowApprovePopup(true);
        } else if (data.status === 'rejected') {
            setShowRejectPopup(true);
        }
    };

    const paginationGroup = usePagination({
        currentPageIndex: page - 1,
        totalPages: data.totalPages,
    });
    
    const pageNumbersElements = paginationGroup.map((p, index) => {
        if (typeof p === 'string') {
            return <span key={`ellipsis-${index}`} className={cn(styles.pageNumberBtn)} style={{cursor: 'default'}}>{p}</span>;
        }
    
        const pageIndex = p;
        return (
            <CustomButton
            key={pageIndex}
            onClick={() => setPage(pageIndex + 1)}
            disabled={page === pageIndex + 1}
            btnType={
                page === pageIndex + 1
                ? ButtonType.ACTIVENOSTYLE
                : ButtonType.NOSTYLE
            }
            btnSize={ButtonSize.NORMAL}
            btnFont={
                page === pageIndex + 1
                ? ButtonFont.BOLD
                : ButtonFont.NORMAL
            }
            className={cn(
                styles.pageNumberBtn,
                page === pageIndex + 1 && styles.activePageNumberBtn
            )}
            >
            {pageIndex + 1}
            </CustomButton>
        );
    });

    return (
        <SidebarLayout>
            <div className={styles.listItemPage}>
                <div className={styles.listItemRowOne}>
                    <TextHeader 
                        title={'Content Management'} 
                        summary={"Manage App Features and Content"}
                        className='mb-4'
                    />
                    <div className={cn(styles.options, 'mb-4')}>
                        <ContentTabs tabs={['All Content', 'My Content', 'Sponsored Content', 'Pending Approval']} 
                            defaultActiveTab={activeContentTab} 
                            tabOnClick={(tab) => {
                                setActiveContentTab(tab);
                                setPage(1); // Reset page on tab change
                            }} 
                        />
                        <SearchInput 
                            placeholder='Search Content' 
                            className={cn(styles.dashboardSearch, 'mb-0')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <div className={styles.selectFilter}>
                            <FilterSelect 
                                value={categoryFilter}
                                options={categoryOptions} 
                                onSelect={(value: string) => {
                                    setCategoryFilter(value);
                                    setPage(1);
                                } } 
                                placeholder={'All Features'} 
                            />
                        </div>
                        <div className={styles.selectFilter}>
                            <FilterSelect 
                                value={statusFilter}
                                options={[
                                    { value: "all", label: "All Status" },
                                    { value: "published", label: "Published" },
                                    { value: "rejected", label: "Rejected" },
                                    { value: "in_review", label: "In Review" },
                                    { value: "draft", label: "Draft" },
                                ]} 
                                onSelect={(value: string) => {
                                    setStatusFilter(value);
                                    setPage(1);
                                } } 
                                placeholder={'Status'} 
                            />
                        </div>
                        <CustomButton 
                            className={styles.addNewContentBtn}
                            btnType={ButtonType.GREEN}
                            onClick={handleScrollToUpload}
                        >
                            <Image src={ASSESTS.ADD} alt={'Download'} /> Add New Content
                        </CustomButton>
                    </div>
                    <div className={styles.contentListWrap}>
                        {dataLoading === 'pending' && Array.from({ length: limit }).map((_, index) => (
                            <ContentManagementCardSkeleton key={index} className={styles.contentListCard} />
                        ))}
                        {dataLoading === 'failed' && (
                            <p className={styles.errorMessage}>Something went wrong!</p>
                        )}
                        {dataLoading === 'succeeded' && data.contentList.length === 0 && !error && (
                            <p className={styles.noResultsMessage}>No results found.</p>
                        )}
                        {dataLoading === 'succeeded' && data.contentList.length > 0 && data.contentList.map((item) => (
                            <ContentManagementCard
                                key={item.id}
                                className={styles.contentListCard}
                                title={item.title}
                                summary={item.desc}
                                bannerFile={item.uploads.length > 0 ? item.uploads[0].fileUrl : ''}
                                status={item.status.toLowerCase()}
                                category= {item.category_list_type}
                                content_type= {item.content_type}
                                userImage={item.user.profile_image || ''}
                                userId= {item.user.id}
                                userName={item.user.full_name}
                                likeCount={item.like_count}
                                shareCount={item.share_count}
                                onView={() => handleView(item)}
                                onApprove={() => handleApprove(item)}
                                onReject={() => handleReject(item)}
                                onDelete={() => handleDelete(item)}
                            />
                        ))}
                    </div>
        
                    <Pagination
                        enteriesShows={`${data.total
                                ? (page - 1) * limit + 1
                                : 0
                            }-${Math.min(
                                (page) * limit,
                                data.total || 0
                            )} of ${data.total || 0} Entries`}
        
                        pageNumbers= {[
                            data.totalPages > 1 && (
                                <CustomButton
                                    key="prev"
                                    onClick={() => setPage(page - 1)}
                                    disabled={page <=1}
                                    btnType={ButtonType.NOSTYLE}
                                    className={styles.pageNumberBtn}
                                >
                                    Prev
                                </CustomButton>
                            ),
                            ...pageNumbersElements,
                            data.totalPages > 1 && (
                                <CustomButton
                                    key="next"
                                    onClick={() => setPage(page + 1)}
                                    disabled={page >= data.totalPages}
                                    btnType={ButtonType.NOSTYLE}
                                    className={styles.pageNumberBtn}
                                >
                                    Next
                                </CustomButton>
                            )
                            ].filter(Boolean)}
                        dropDownOptions={[
                            { value: "5", label: "5" },
                            { value: "10", label: "10" },
                            { value: "20", label: "20" },
                            { value: "25", label: "25" },
                        ]}
                        dropdownOnChange={(value) => {
                            setLimit(Number(value));
                            setPage(1);
                        }}
                        dropDownPlaceholder={limit.toString()}
                    />
                </div>
                <div ref={uploadRef} className={cn(styles.listItemRowTwo)}>
                    <UploadNewContent />
                </div>
            </div>
            <DeleteModal 
                title={'Delete List Item'}
                showModal={showDeleteModal}
                onClose={() => setShowDeleteModal(false)} 
                bodyText={'Are you sure want to delete this Item?'} 
                onDelete={confirmDelete}
            />
            {showReviewPopup && (
                <ReviewPopup 
                    content={contentDetails}
                    loading={contentDetailsLoading === 'pending'}
                    showModal={showReviewPopup} 
                    onHide={() => setShowReviewPopup(false)}
                    onUpdate={handleUpdateFromPopup}
                    onDelete={handleDelete}
                />
            )}
            <ConfirmationModal 
                title={'Approve Content'}
                showModal={showApprovePopup}
                onClose={() => {
                    setShowApprovePopup(false);
                    setContentToActOn(null);
                    setActionData(null);
                }} 
                bodyText={'Are you sure you want to approve this content?'} 
                cancelBtnText='Cancel'
                submitBtnText={contentUpdateLoading === 'pending' ? 'Approving...' : 'Approve'}
                onSubmit={confirmApprove}
                isSubmitDisabled={contentUpdateLoading === 'pending'}
            />
            <ConfirmationModal 
                title={'Reject Content'}
                showModal={showRejectPopup}
                onClose={() => {
                    setShowRejectPopup(false);
                    setContentToActOn(null);
                    setActionData(null);
                }} 
                bodyText={'Are you sure you want to reject this content?'} 
                cancelBtnText='Cancel'
                submitBtnText={contentUpdateLoading === 'pending' ? 'Rejecting...' : 'Reject'}
                submitBtnType= {ButtonType.RED}
                onSubmit={confirmReject}
                isSubmitDisabled={contentUpdateLoading === 'pending'}
            />
        </SidebarLayout>
    );
};

export default ListIcons;